import * as React from 'react';
import { useEffect, useState } from 'react';

import { COLOR_BASE_BRAND_PROGRESSIVE_RED } from '@audi/audi-ui-design-tokens';
import styled from 'styled-components';
import { DangerousElement, CodeDescription } from '@oneaudi/vtp-shared';
import { Text } from '@audi/audi-ui-react-v2';
import { useI18n } from '@oneaudi/i18n-context';

import { HighlightWrap, CommonHighlight } from './CommonStyledComponents';
import { messages } from '../i18n/messages';
import {
  HighLightItem,
  warrantyCodeLookup,
  WarrantyNameTypes,
} from '../interface/highlights.interfaces';
import HighlightInfo from './HighlightInfo';
import WarrantyImg from './WarrantyImg';

interface HighlightListIconProps {
  item: HighLightItem;
  warranties: CodeDescription[] | undefined;
}

const WarrantyHighlight = styled(CommonHighlight)`
  .sc-audi-red-plus {
    color: ${COLOR_BASE_BRAND_PROGRESSIVE_RED};
    font-weight: var(${(props): string => props.theme.fontWeights.bold});
  }
`;

const hasWarranty = (
  warranties: CodeDescription[] = [],
  warrantyName: WarrantyNameTypes | string,
): boolean => {
  return (
    warranties?.filter(
      // @ts-ignore
      (warranty: CodeDescription) => warrantyCodeLookup[warrantyName] === warranty.code,
    ).length > 0
  );
};

const WarrantyItem: React.FC<HighlightListIconProps> = ({ item, warranties }) => {
  const { IconLabel: ItemLabel, InfoLink, TechdataKey } = item;
  const defaultLabel = useI18n(messages.warrantyLabel);
  const hasWarrantyLabel = useI18n(messages.warrantyYes);
  const [warrantyLabel, setWarrantyLabel] = useState<string>();
  const [warrantyDescription, setWarrantyDescription] = useState<string>();
  const showWarrantyImage = [
    'WarrantyTypeNap',
    'WarrantyTypePlus',
    'WarrantyType5Years',
    'WarrantyTypeAsgExtended',
  ].includes(TechdataKey);
  let audiPlusLabel = ItemLabel;

  if (['WarrantyTypePlus', 'WarrantyType5Years', 'WarrantyTypeAsgExtended'].includes(TechdataKey)) {
    const spanLessLabel = ItemLabel?.replace(/<\s*\w+[^>]*>|<\s*\/\w+\s*>/g, '') || '';
    audiPlusLabel = spanLessLabel!
      .replace(':plus', '<span class="sc-audi-red-plus">:plus</span>')
      .replace('Audi', '<span class="sc-audi-red-plus">Audi</span>');
  }

  useEffect(() => {
    switch (true) {
      case TechdataKey.includes('WarrantyTypePlus'):
        setWarrantyLabel(audiPlusLabel);
        break;
      case TechdataKey.includes('WarrantyType5Years'):
        setWarrantyLabel(audiPlusLabel);
        break;
      case TechdataKey.includes('WarrantyTypeAsgExtended'):
        setWarrantyLabel(audiPlusLabel);
        break;
      case TechdataKey.includes('WarrantyTypeNap'):
        setWarrantyLabel(ItemLabel);
        break;
      case TechdataKey.includes('WarrantyTypeWarranty'):
        setWarrantyLabel(ItemLabel);
        setWarrantyDescription(hasWarrantyLabel);
        break;
      default:
        setWarrantyLabel(defaultLabel);
        setWarrantyDescription(ItemLabel);
    }
  }, [warrantyLabel, TechdataKey, defaultLabel, ItemLabel, audiPlusLabel, hasWarrantyLabel]);
  return (
    <>
      {hasWarranty(warranties, TechdataKey) && (
        <>
          <HighlightWrap className="sc-detail-highlight-wrapper" data-tech-key={TechdataKey}>
            <WarrantyHighlight className="sc-detail-highlight" data-tech-key={TechdataKey}>
              <Text as="p" variant="copy2" spaceStackEnd="xs" className="sc-detail-highlight-label">
                <DangerousElement html={warrantyLabel!} as="span" />
              </Text>
              {showWarrantyImage ? (
                <WarrantyImg type={TechdataKey} />
              ) : (
                <>
                  <Text as="span" variant="copy1" weight="bold" spaceStackEnd="xs">
                    <DangerousElement html={warrantyDescription!} as="span" />
                  </Text>
                </>
              )}
              {InfoLink ? <HighlightInfo infoLink={InfoLink} /> : ''}
            </WarrantyHighlight>
          </HighlightWrap>
        </>
      )}
    </>
  );
};
export default WarrantyItem;
